/**
 * Reading what an event badge's code holds (G-163).
 *
 * Badge QR codes at trade shows are nearly always a vCard or a MECARD; a
 * handheld scanner types the payload into whatever field has focus. This turns
 * that text into the fields of a capture form, for a person at the booth to
 * check before saving - so it reads what it can and leaves the rest blank,
 * rather than guessing a name from an email address.
 */

export interface BadgeFields {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  title: string | null;
}

const EMPTY: BadgeFields = { firstName: null, lastName: null, email: null, phone: null, company: null, title: null };

/** vCard escapes: \, \; \n. */
const unescape = (value: string): string =>
  value.replace(/\\n/gi, ' ').replace(/\\([,;\\])/g, '$1').trim();

const clean = (value: string | undefined | null): string | null => {
  const text = (value ?? '').trim();
  return text === '' ? null : text;
};

function splitName(full: string | null): Pick<BadgeFields, 'firstName' | 'lastName'> {
  if (!full) return { firstName: null, lastName: null };
  const parts = full.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0] ?? null, lastName: null };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] ?? null };
}

function fromVCard(text: string): BadgeFields {
  // Folded lines (a newline then a space) are one line.
  const lines = text.replace(/\r?\n[ \t]/g, '').split(/\r?\n/);
  const fields: BadgeFields = { ...EMPTY };
  let formatted: string | null = null;
  for (const line of lines) {
    const colon = line.indexOf(':');
    if (colon < 0) continue;
    const key = (line.slice(0, colon).split(';')[0] ?? '').toUpperCase().replace(/^ITEM\d+\./, '');
    const value = line.slice(colon + 1);
    if (key === 'N') {
      const [last, first] = value.split(';');
      fields.lastName = clean(unescape(last ?? ''));
      fields.firstName = clean(unescape(first ?? ''));
    } else if (key === 'FN') formatted = clean(unescape(value));
    else if (key === 'EMAIL' && !fields.email) fields.email = clean(value);
    else if (key === 'TEL' && !fields.phone) fields.phone = clean(value.replace(/^tel:/i, ''));
    else if (key === 'ORG') fields.company = clean(unescape(value.split(';')[0] ?? ''));
    else if (key === 'TITLE') fields.title = clean(unescape(value));
  }
  if (!fields.firstName && !fields.lastName) Object.assign(fields, splitName(formatted));
  return fields;
}

function fromMeCard(text: string): BadgeFields {
  const body = text.replace(/^MECARD:/i, '');
  const fields: BadgeFields = { ...EMPTY };
  for (const part of body.split(/(?<!\\);/)) {
    const colon = part.indexOf(':');
    if (colon < 0) continue;
    const key = part.slice(0, colon).toUpperCase();
    const value = unescape(part.slice(colon + 1));
    if (key === 'N') {
      // MECARD writes "Last,First".
      const [last, first] = value.split(',');
      fields.lastName = clean(last);
      fields.firstName = clean(first);
    } else if (key === 'EMAIL' && !fields.email) fields.email = clean(value);
    else if (key === 'TEL' && !fields.phone) fields.phone = clean(value);
    else if (key === 'ORG') fields.company = clean(value);
    else if (key === 'TITLE') fields.title = clean(value);
  }
  return fields;
}

/** Reads a scanned badge. Unknown formats give back only an email address, if one is in the text. */
export function parseBadge(raw: string): BadgeFields {
  const text = raw.trim();
  if (/^BEGIN:VCARD/i.test(text)) return fromVCard(text);
  if (/^MECARD:/i.test(text)) return fromMeCard(text);
  const email = /[^\s;,<>"]+@[^\s;,<>"]+\.[a-z]{2,}/i.exec(text)?.[0] ?? null;
  return { ...EMPTY, email };
}
